import { audio } from '../../../../audio/AudioManager.js';
import { SaveManager } from '../../../../state/SaveManager.js';
import { NUMBER_KINGDOM_ASSETS } from '../NumberKingdomAssets.js';
import {
  calculateChallengeSafeBounds,
  isCompactChallengeLayout,
  type ChallengeSafeBounds,
} from './NumberKingdomChallengeLayout.js';
import type { NumberPathConfig } from './NumberPathConfig.js';
import { NumberPathState } from './NumberPathState.js';

const FONT_UI = '"Nunito", "Segoe UI", Arial, sans-serif';

interface NumberPathCallbacks {
  onCancel: () => void;
  onComplete: (config: NumberPathConfig) => void;
}

interface NumberPathLayout {
  compact: boolean;
  safe: ChallengeSafeBounds;
  panelY: number;
  panelWidth: number;
  panelHeight: number;
  pathY: number;
  pathStartX: number;
  pathEndX: number;
  nodeWidth: number;
  nodeHeight: number;
  finishY: number;
  feedbackY: number;
  buttonY: number;
}

export class NumberPathChallenge {
  private readonly state: NumberPathState;
  private readonly container: Phaser.GameObjects.Container;
  private readonly nodes: Phaser.GameObjects.Image[] = [];
  private readonly nodeLabels: Phaser.GameObjects.Text[] = [];
  private readonly timers: Phaser.Time.TimerEvent[] = [];
  private panel!: Phaser.GameObjects.Image;
  private backButton!: Phaser.GameObjects.Text;
  private controlsText!: Phaser.GameObjects.Text;
  private startLabel!: Phaser.GameObjects.Text;
  private targetLabel!: Phaser.GameObjects.Text;
  private moveText!: Phaser.GameObjects.Text;
  private feedbackText!: Phaser.GameObjects.Text;
  private jumpTrail!: Phaser.GameObjects.Image;
  private finishCrystal!: Phaser.GameObjects.Image;
  private orb!: Phaser.GameObjects.Image;
  private successBurst!: Phaser.GameObjects.Image;
  private plusTenButton!: Phaser.GameObjects.Image;
  private plusOneButton!: Phaser.GameObjects.Image;
  private resetButton!: Phaser.GameObjects.Image;
  private hintButton!: Phaser.GameObjects.Image;
  private currentLayout!: NumberPathLayout;
  private hintIndex = 0;
  private terminalAttemptRecorded = false;
  private inputLocked = false;
  private suspended = false;
  private destroyed = false;

  constructor(
    private readonly scene: Phaser.Scene,
    readonly config: NumberPathConfig,
    private readonly reducedMotion: boolean,
    private readonly callbacks: NumberPathCallbacks,
  ) {
    this.state = new NumberPathState(config);
    this.container = scene.add.container(0, 0).setDepth(220).setAlpha(0);
    this.createInterface();
    this.registerKeyboard();
    this.scene.events.once(Phaser.Scenes.Events.SHUTDOWN, this.destroy, this);
    this.scene.tweens.add({
      targets: this.container,
      alpha: 1,
      duration: this.reducedMotion ? 160 : 320,
      ease: 'Sine.easeOut',
    });
  }

  private createInterface() {
    this.panel = this.scene.add.image(640, 160, NUMBER_KINGDOM_ASSETS.numberPathChallengePanel.key);
    this.panel.setCrop(6, 0, 510, 316);

    this.backButton = this.scene.add.text(205, 22, '←  BACK TO VALLEY', {
      fontFamily: FONT_UI,
      fontSize: '14px',
      fontStyle: 'bold',
      color: '#fff8dd',
      stroke: '#17351f',
      strokeThickness: 5,
      padding: { x: 10, y: 8 },
    }).setOrigin(0.5, 0).setInteractive({ useHandCursor: true });
    this.backButton.on('pointerup', () => this.requestCancel());

    this.controlsText = this.scene.add.text(640, 22,
      'Choose +10 or +1  •  1 / 2 jump  •  Z / Backspace undo  •  R reset  •  H hint', {
        fontFamily: FONT_UI,
        fontSize: '12px',
        fontStyle: 'bold',
        color: '#fff8dd',
        stroke: '#17351f',
        strokeThickness: 4,
      }).setOrigin(0.5, 0);

    this.startLabel = this.scene.add.text(590, 151, `START  ${this.config.start}`, {
      fontFamily: FONT_UI, fontSize: '17px', fontStyle: 'bold', color: '#ffffff',
      backgroundColor: '#087cc5', stroke: '#07356d', strokeThickness: 4,
      padding: { x: 9, y: 5 },
    }).setOrigin(1, 0.5);
    this.targetLabel = this.scene.add.text(690, 151, `REACH  ${this.config.target}`, {
      fontFamily: FONT_UI, fontSize: '17px', fontStyle: 'bold', color: '#ffffff',
      backgroundColor: '#6d2098', stroke: '#32105e', strokeThickness: 4,
      padding: { x: 9, y: 5 },
    }).setOrigin(0, 0.5);

    this.jumpTrail = this.scene.add.image(640, 413, NUMBER_KINGDOM_ASSETS.numberPathJumpTrail.key)
      .setAlpha(0.8);
    this.jumpTrail.setCrop(0, 4, 546, 178);

    this.finishCrystal = this.scene.add.image(1010, 355, NUMBER_KINGDOM_ASSETS.pathFinishCrystal.key)
      .setAlpha(0.9);
    this.finishCrystal.setCrop(5, 14, 300, 196);

    for (let index = 0; index <= this.config.maxJumps; index++) {
      const node = this.scene.add.image(300 + index * 170, 420, NUMBER_KINGDOM_ASSETS.numberPathNode.key);
      this.nodes.push(node);
      const label = this.scene.add.text(node.x, node.y + 2, index === 0 ? String(this.config.start) : '—', {
        fontFamily: FONT_UI,
        fontSize: '29px',
        fontStyle: 'bold',
        color: '#ffffff',
        backgroundColor: '#073f86',
        stroke: '#071d4d',
        strokeThickness: 5,
        padding: { x: 8, y: 2 },
      }).setOrigin(0.5);
      this.nodeLabels.push(label);
    }

    this.orb = this.scene.add.image(300, 369, NUMBER_KINGDOM_ASSETS.numberPathEnergyOrb.key)
      .setAlpha(0.96);
    this.orb.setCrop(8, 4, 222, 224);

    this.successBurst = this.scene.add.image(1010, 410, NUMBER_KINGDOM_ASSETS.numberPathSuccessBurst.key)
      .setVisible(false).setAlpha(0);
    this.successBurst.setCrop(18, 18, 275, 252);

    this.moveText = this.scene.add.text(640, 516, '', {
      fontFamily: FONT_UI, fontSize: '19px', fontStyle: 'bold', color: '#fff8cf',
      stroke: '#17351f', strokeThickness: 5,
    }).setOrigin(0.5);
    this.feedbackText = this.scene.add.text(640, 550, 'Plan a path from 126 to 148 in four jumps.', {
      fontFamily: FONT_UI, fontSize: '17px', fontStyle: 'bold', align: 'center', color: '#fff2a6',
      stroke: '#17351f', strokeThickness: 5, wordWrap: { width: 760 },
    }).setOrigin(0.5);

    this.plusTenButton = this.createImageButton(NUMBER_KINGDOM_ASSETS.jumpPlusTen.key, () => this.tryJump(10));
    this.plusOneButton = this.createImageButton(NUMBER_KINGDOM_ASSETS.jumpPlusOne.key, () => this.tryJump(1));
    this.resetButton = this.createImageButton(NUMBER_KINGDOM_ASSETS.numberPathResetButton.key, () => this.resetPath());
    this.resetButton.setCrop(10, 5, 250, 104);
    this.hintButton = this.createImageButton(NUMBER_KINGDOM_ASSETS.numberPathHintButton.key, () => this.showHint());
    this.hintButton.setCrop(10, 5, 245, 104);

    this.container.add([
      this.panel, this.backButton, this.controlsText, this.startLabel, this.targetLabel,
      this.jumpTrail, this.finishCrystal, ...this.nodes, ...this.nodeLabels, this.orb,
      this.successBurst, this.moveText, this.feedbackText, this.plusTenButton,
      this.plusOneButton, this.resetButton, this.hintButton,
    ]);
    this.layout();
    this.renderPath();
  }

  private createImageButton(texture: string, callback: () => void) {
    const button = this.scene.add.image(640, 640, texture).setInteractive({ useHandCursor: true });
    button.on('pointerover', () => {
      if (this.canInteract()) button.setScale(button.scaleX * 1.025, button.scaleY * 1.025);
    });
    button.on('pointerout', () => this.layout());
    button.on('pointerup', callback);
    return button;
  }

  layout() {
    if (this.destroyed || !this.panel) return;
    const safe = calculateChallengeSafeBounds(this.scene);
    const compact = isCompactChallengeLayout(this.scene, safe);
    const layout: NumberPathLayout = {
      compact,
      safe,
      panelY: compact ? safe.y + 128 : 164,
      panelWidth: compact ? 410 : 465,
      panelHeight: compact ? 254 : 288,
      pathY: compact ? safe.y + 365 : 420,
      pathStartX: safe.x + (compact ? 92 : 115),
      pathEndX: safe.right - (compact ? 92 : 115),
      nodeWidth: compact ? 114 : 136,
      nodeHeight: compact ? 91 : 108,
      finishY: compact ? safe.y + 300 : 355,
      feedbackY: compact ? safe.bottom - 130 : 550,
      buttonY: safe.bottom - (compact ? 58 : 70),
    };
    this.currentLayout = layout;

    this.panel.setPosition(safe.centerX, layout.panelY).setDisplaySize(layout.panelWidth, layout.panelHeight);
    this.backButton.setPosition(Math.max(205, safe.x + 90), compact ? safe.y + 2 : 22);
    this.controlsText.setPosition(safe.centerX, compact ? safe.y + 2 : 22).setFontSize(compact ? 10 : 12);
    this.startLabel.setPosition(safe.centerX - (compact ? 36 : 50), layout.panelY - 12)
      .setFontSize(compact ? 14 : 17);
    this.targetLabel.setPosition(safe.centerX + (compact ? 36 : 50), layout.panelY - 12)
      .setFontSize(compact ? 14 : 17);

    const span = layout.pathEndX - layout.pathStartX;
    const nodeXs = this.nodes.map((_node, index) => layout.pathStartX + span * index / this.config.maxJumps);
    this.jumpTrail.setPosition(safe.centerX, layout.pathY - 3)
      .setDisplaySize(span + layout.nodeWidth, compact ? 100 : 120);
    this.finishCrystal.setPosition(layout.pathEndX, layout.finishY)
      .setDisplaySize(compact ? 145 : 170, compact ? 95 : 111);

    this.nodes.forEach((node, index) => {
      node.setPosition(nodeXs[index], layout.pathY).setDisplaySize(layout.nodeWidth, layout.nodeHeight);
      this.nodeLabels[index].setPosition(nodeXs[index], layout.pathY + 2).setFontSize(compact ? 23 : 29);
    });
    const activeIndex = this.state.movesUsed;
    this.orb.setPosition(nodeXs[activeIndex], layout.pathY - (compact ? 51 : 61))
      .setDisplaySize(compact ? 62 : 74, compact ? 62 : 74);
    this.successBurst.setPosition(layout.pathEndX, layout.pathY)
      .setDisplaySize(compact ? 160 : 190, compact ? 147 : 174);

    this.moveText.setPosition(safe.centerX, layout.feedbackY - (compact ? 36 : 34)).setFontSize(compact ? 16 : 19);
    this.feedbackText.setPosition(safe.centerX, layout.feedbackY).setFontSize(compact ? 14 : 17)
      .setWordWrapWidth(Math.min(760, safe.width - 70));

    const buttonGap = compact ? 128 : 150;
    this.plusTenButton.setPosition(safe.centerX - buttonGap * 1.5, layout.buttonY)
      .setDisplaySize(compact ? 104 : 122, compact ? 102 : 120);
    this.plusOneButton.setPosition(safe.centerX - buttonGap * 0.5, layout.buttonY)
      .setDisplaySize(compact ? 104 : 122, compact ? 102 : 120);
    this.resetButton.setPosition(safe.centerX + buttonGap * 0.65, layout.buttonY)
      .setDisplaySize(compact ? 145 : 165, compact ? 63 : 72);
    this.hintButton.setPosition(safe.centerX + buttonGap * 1.75, layout.buttonY)
      .setDisplaySize(compact ? 140 : 160, compact ? 60 : 70);
  }

  private tryJump(jump: number) {
    if (!this.canInteract()) return;
    const priorIndex = this.state.movesUsed;
    const result = this.state.addJump(jump);
    if (result === 'overshoot') {
      audio.hint();
      this.feedbackText.setText(`${this.state.value} + ${jump} goes past ${this.config.target}. Try a smaller jump.`);
      return;
    }
    if (result === 'full') {
      this.feedbackText.setText('All four jumps are placed. Undo one or reset to try a new path.');
      return;
    }
    if (result !== 'accepted' && result !== 'complete') return;

    audio.click();
    this.terminalAttemptRecorded = false;
    this.renderPath();
    const nextNode = this.nodes[this.state.movesUsed];
    this.inputLocked = true;
    this.scene.tweens.killTweensOf(this.orb);
    this.orb.setPosition(this.nodes[priorIndex].x, this.currentLayout.pathY - (this.currentLayout.compact ? 51 : 61));
    this.scene.tweens.add({
      targets: this.orb,
      x: nextNode.x,
      y: this.currentLayout.pathY - (this.currentLayout.compact ? 51 : 61),
      duration: this.reducedMotion ? 100 : 420,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        this.inputLocked = false;
        if (this.state.isComplete) this.playSuccess();
        else if (this.state.isFull) this.handleUnfinishedPath();
      },
    });
  }

  private renderPath() {
    const values = this.state.values;
    this.nodes.forEach((node, index) => {
      const visited = index <= this.state.movesUsed;
      node.setTexture(visited ? NUMBER_KINGDOM_ASSETS.numberPathNodeActive.key : NUMBER_KINGDOM_ASSETS.numberPathNode.key);
      const label = this.nodeLabels[index];
      label.setText(visited ? String(values[index]) : '—');
      label.setBackgroundColor(visited ? '#451070' : '#073f86');
    });
    const jumps = this.state.jumps.map(value => `+${value}`).join('  →  ');
    this.moveText.setText(
      jumps
        ? `${jumps}   •   ${this.state.movesUsed}/${this.config.maxJumps} jumps`
        : `0/${this.config.maxJumps} jumps used`,
    );
    this.resetButton.setAlpha(this.state.movesUsed > 0 ? 1 : 0.6);
  }

  private handleUnfinishedPath() {
    if (!this.terminalAttemptRecorded) {
      SaveManager.recordAnswer(this.config.skillId, false);
      this.terminalAttemptRecorded = true;
    }
    this.feedbackText.setText(`You reached ${this.state.value}. Keep this path, then undo or reset when you are ready.`);
  }

  private undo() {
    if (!this.canInteract() || !this.state.undo()) return;
    audio.click();
    this.terminalAttemptRecorded = false;
    this.feedbackText.setText(`Back to ${this.state.value}. Choose your next jump.`);
    this.renderPath();
    this.layout();
  }

  private resetPath() {
    if (!this.canInteract() || !this.state.reset()) return;
    audio.click();
    this.terminalAttemptRecorded = false;
    this.feedbackText.setText(`Path reset. Start again at ${this.config.start}.`);
    this.renderPath();
    this.layout();
  }

  private showHint() {
    if (!this.canInteract()) return;
    const hints = [
      `${this.config.target} is 22 more than ${this.config.start}.`,
      'Two +10 jumps will move you from 126 to 146.',
      'Then two +1 jumps reach 148 in exactly four jumps.',
    ];
    this.feedbackText.setText(`Hint: ${hints[Math.min(this.hintIndex, hints.length - 1)]}`);
    this.hintIndex = Math.min(this.hintIndex + 1, hints.length - 1);
    audio.hint();
  }

  private playSuccess() {
    this.inputLocked = true;
    SaveManager.recordAnswer(this.config.skillId, true);
    audio.correct();
    this.feedbackText.setText(`You reached ${this.config.target}! The second bridge repair is ready.`);
    this.successBurst.setVisible(true).setAlpha(0).setScale(this.reducedMotion ? 1 : 0.82);
    this.container.bringToTop(this.successBurst);
    this.container.bringToTop(this.orb);
    this.scene.tweens.add({
      targets: this.successBurst,
      alpha: 1,
      scale: 1,
      duration: this.reducedMotion ? 170 : 430,
      ease: 'Back.easeOut',
    });
    this.timers.push(this.scene.time.delayedCall(this.reducedMotion ? 720 : 1250, () => {
      const config = this.config;
      this.destroy();
      this.callbacks.onComplete(config);
    }));
  }

  private registerKeyboard() {
    this.scene.input.keyboard?.on('keydown', this.handleKeyDown, this);
  }

  private handleKeyDown(event: KeyboardEvent) {
    if (!this.canInteract()) return;
    if (event.code === 'Digit1' || event.code === 'Numpad1') {
      event.preventDefault();
      this.tryJump(10);
    } else if (event.code === 'Digit2' || event.code === 'Numpad2') {
      event.preventDefault();
      this.tryJump(1);
    } else if (event.code === 'Backspace' || event.code === 'KeyZ') {
      event.preventDefault();
      this.undo();
    } else if (event.code === 'KeyR') {
      event.preventDefault();
      this.resetPath();
    } else if (event.code === 'KeyH') {
      event.preventDefault();
      this.showHint();
    } else if (event.code === 'Escape') {
      event.preventDefault();
      this.requestCancel();
    }
  }

  private requestCancel() {
    if (this.destroyed || this.inputLocked) return;
    this.destroy();
    this.callbacks.onCancel();
  }

  setSuspended(suspended: boolean) { this.suspended = suspended; }

  private canInteract() {
    return !this.destroyed && !this.inputLocked && !this.suspended;
  }

  destroy() {
    if (this.destroyed) return;
    this.destroyed = true;
    this.scene.input.keyboard?.off('keydown', this.handleKeyDown, this);
    this.scene.events.off(Phaser.Scenes.Events.SHUTDOWN, this.destroy, this);
    this.timers.forEach(timer => timer.remove(false));
    this.timers.length = 0;
    this.scene.tweens.killTweensOf(this.container.getAll());
    this.scene.tweens.killTweensOf(this.container);
    this.container.destroy(true);
  }
}
